import React, { useMemo } from 'react';
import classNames from 'classnames/bind';
import { get } from 'lodash';
import { SortOutlinedIcon } from 'tuui';
import { Menu } from 'tuui';
import { SORTING_ORDER_TYPES } from 'taboola-ultimate-ui';
import { CAMPAIGN_SORTABLE_FIELDS } from '../../../../modules/campaigns/config';
import { FormattedMessage } from '../../../../modules/taboola-common-frontend-modules/i18n';
import { useCampaignPickerSorting } from '../../hooks/useCampaignPickerSorting';
import { useNodeWindowOverflow } from '../../hooks/useNodeWindowOverflow';
import { BreadcrumbSortItem } from '../BreadcrumbSortItem/BreadcrumbSortItem';
import styles from './CampaignSorting.module.scss';

const classNameBuilder = classNames.bind(styles);

const dropdownRequiredWidth = 180;
const sortFieldsOptions = [
    {
        value: CAMPAIGN_SORTABLE_FIELDS.TOTAL_SPENT,
        label: <FormattedMessage id="campaign.picker.sorting.totalSpent" defaultMessage="Spent (Overall)" />,
    },
    {
        value: CAMPAIGN_SORTABLE_FIELDS.CREATE_DATE,
        label: <FormattedMessage id="campaign.picker.sorting.creationDate" defaultMessage="Creation Date" />,
    },
    {
        value: CAMPAIGN_SORTABLE_FIELDS.NAME,
        label: <FormattedMessage id="campaign.picker.sorting.name" defaultMessage="Name" />,
    },
];
export const menuConfig = {
    offsetY: 37,
    offsetX: -35,
    menuClassName: styles['menu'],
    submenuProps: {
        offsetX: 8.5,
        menuClassName: classNameBuilder(styles['sub-menu']),
        itemProps: {
            className: classNameBuilder(styles['menu-item']),
        },
    },
    menuItemProps: {
        className: classNameBuilder(styles['menu-item']),
    },
};

const SortIcon = () => <SortOutlinedIcon className={styles['sorting-icon']} />;

const Title = () => (
    <FormattedMessage id="app.breadcrumbs.sort" defaultMessage="Sort" className={styles['sort-title']} />
);

const SortItem = ({ item }) => {
    const { sort } = useCampaignPickerSorting();
    return <BreadcrumbSortItem item={item} sort={sort} />;
};

export const CampaignSorting = ({ sort, onSort }) => {
    const currentSortField = get(sort, '0.field');
    const currentOrderField = get(sort, '0.order');

    const { containerRef, overflowWidth } = useNodeWindowOverflow({
        requiredWidth: dropdownRequiredWidth,
    });

    const handleSorting = (order, field) => {
        if (currentSortField === field && currentOrderField === order) {
            return;
        }
        onSort([{ field, order }]);
    };

    const formattedSortFieldsOptions = useMemo(
        () =>
            sortFieldsOptions.map(field => ({
                label: field.label,
                value: field.value,
                subItems: [
                    {
                        label: (
                            <FormattedMessage id={`app.breadcrumbs.${field.value}.${SORTING_ORDER_TYPES.ASCENDANT}`} />
                        ),
                        value: SORTING_ORDER_TYPES.ASCENDANT,
                    },
                    {
                        label: (
                            <FormattedMessage id={`app.breadcrumbs.${field.value}.${SORTING_ORDER_TYPES.DESCENDANT}`} />
                        ),
                        value: SORTING_ORDER_TYPES.DESCENDANT,
                    },
                ],
            })),
        []
    );

    return (
        <div className={styles['sorting-container']} ref={containerRef}>
            <Menu
                items={formattedSortFieldsOptions}
                onSelect={handleSorting}
                ItemComponent={SortItem}
                MenuButtonComponent={SortIcon}
                TitleComponent={Title}
                direction={overflowWidth ? 'left' : 'right'}
                {...menuConfig}
            />
        </div>
    );
};
